// Global Instructions Rule Applied!
// Shapes of the Interest Accruals workbench (docs/INTEREST_ACCRUAL_DESIGN.md
// section 6.1):
//   GET /interest-accruals/fund/:fundId/position?as_of=&side=
//     at__interest_accrual_position - one row per rated tranche of the fund
//     (receivable: the interest the fund earns on its notes) or per fund loan
//     (payable: the interest the fund owes), with the schedule behind each
//     row, the totals, the journal preview over the bookable rows and the
//     warnings. Nothing is written.
//   GET /interest-accruals/fund/:fundId/runs      at__interest_runs
//   GET /interest-accruals/runs/:id               at__interest_run_detail
//   POST unbook / DELETE                          {status, dry_run, transactions_affected}
// Ids are plain z.string() (Dataverse GUIDs are not RFC 4122).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const INTEREST_SIDES = ['receivable', 'payable'] as const
export type InterestSide = (typeof INTEREST_SIDES)[number]

export const InterestSideSchema = z.enum(INTEREST_SIDES)

// ---------------------------------------------------------------------------
// Position
// ---------------------------------------------------------------------------

export const InterestLastRunSchema = z
  .object({
    asset_interest_runs__id: z.string(),
    as_of_date: z.string().nullish(),
    side: z.string().nullish(),
    status_label: z.string().nullish(),
    line_count: z.number().nullish(),
    total_amount_functional: z.number().nullish()
  })
  .passthrough()

export const InterestAccrualPositionFundSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    // the fund's functional currency
    currencies__code: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    side: InterestSideSchema,
    as_of_date: z.string().nullish(),
    // the latest non-trashed run of this side
    last_run: InterestLastRunSchema.nullish()
  })
  .passthrough()

export const InterestAccrualTotalsSchema = z
  .object({
    line_count: z.number().nullish(),
    bookable_count: z.number().nullish(),
    this_booking_functional: z.number().nullish()
  })
  .passthrough()

// functional currency, over the bookable rows' calculated amounts; a null
// account = no account on the fund's chart for the item (a warning says so)
export const InterestJournalPreviewLineSchema = z
  .object({
    acc_items__label: z.string().nullish(),
    acc_chart_accounts__number: z.string().nullish(),
    acc_chart_accounts__name: z.string().nullish(),
    prog__debit: z.number().nullish(),
    prog__credit: z.number().nullish()
  })
  .passthrough()

// at__interest_periods(tranche, as_of + 1)
export const ReceivableScheduleRowSchema = z
  .object({
    period_start: z.string().nullish(),
    period_end: z.string().nullish(),
    days: z.number().nullish(),
    principal: z.number().nullish(),
    capitalised_interest: z.number().nullish(),
    interest_rate: z.number().nullish(),
    day_count_label: z.string().nullish(),
    interest: z.number().nullish(),
    capitalises: z.boolean().nullish()
  })
  .passthrough()

// loan__interest_periods(loan, as_of + 1)
export const PayableScheduleRowSchema = ReceivableScheduleRowSchema.extend({
  charge_rate: z.number().nullish(),
  charge: z.number().nullish()
}).passthrough()

export const RECEIVABLE_STATUSES = [
  'accrued',
  'cash_pay',
  'matured_accruing',
  'exited',
  'no_terms',
  'already_accrued'
] as const
export type ReceivableStatus = (typeof RECEIVABLE_STATUSES)[number]

export const PAYABLE_STATUSES = ['accrued', 'matured', 'already_accrued'] as const
export type PayableStatus = (typeof PAYABLE_STATUSES)[number]

export const InterestAccrualReceivableRowSchema = z
  .object({
    tranches__id: z.string(),
    tranches__name: z.string().nullish(),
    securities__id: z.string().nullish(),
    securities__name: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    // the SECURITY currency
    currencies__code: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    // units held at the close of the as-of date
    prog__principal: z.number().nullish(),
    // the rate in force at the as-of date, a fraction (0.12 = 12%)
    prog__rate: z.number().nullish(),
    prog__terms_label: z.string().nullish(),
    // null = the security has no interest terms
    prog__accrued_to_date: z.number().nullish(),
    prog__booked_before: z.number().nullish(),
    prog__booked_balance: z.number().nullish(),
    prog__this_booking: z.number().nullish(),
    // null = the FX feed has no rate for the accrual date
    prog__fx_rate: z.number().nullish(),
    prog__this_booking_functional: z.number().nullish(),
    prog__accrued_to: z.string().nullish(),
    prog__status: z.enum(RECEIVABLE_STATUSES),
    prog__status_label: z.string().nullish(),
    prog__bookable: z.boolean().nullish(),
    schedule: z.array(ReceivableScheduleRowSchema).nullish()
  })
  .passthrough()

export const InterestAccrualPayableRowSchema = z
  .object({
    fund_loans__id: z.string(),
    fund_loans__reference: z.string().nullish(),
    fund_loans__name: z.string().nullish(),
    currencies__code: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    prog__principal: z.number().nullish(),
    prog__rate: z.number().nullish(),
    prog__charge_rate: z.number().nullish(),
    prog__terms_label: z.string().nullish(),
    prog__interest_accrued_to_date: z.number().nullish(),
    prog__charge_accrued_to_date: z.number().nullish(),
    prog__interest_booked_before: z.number().nullish(),
    prog__charge_booked_before: z.number().nullish(),
    prog__this_interest: z.number().nullish(),
    prog__this_charge: z.number().nullish(),
    prog__this_booking: z.number().nullish(),
    prog__fx_rate: z.number().nullish(),
    prog__this_booking_functional: z.number().nullish(),
    prog__accrued_to: z.string().nullish(),
    prog__status: z.enum(PAYABLE_STATUSES),
    prog__status_label: z.string().nullish(),
    prog__bookable: z.boolean().nullish(),
    schedule: z.array(PayableScheduleRowSchema).nullish()
  })
  .passthrough()

const positionBase = {
  fund: InterestAccrualPositionFundSchema,
  totals: InterestAccrualTotalsSchema.nullish(),
  journal_preview: z.array(InterestJournalPreviewLineSchema).nullish(),
  warnings: z.array(z.string()).nullish()
}

export const ReceivablePositionSchema = z
  .object({ ...positionBase, rows: z.array(InterestAccrualReceivableRowSchema) })
  .passthrough()

export const PayablePositionSchema = z
  .object({ ...positionBase, rows: z.array(InterestAccrualPayableRowSchema) })
  .passthrough()

export type InterestLastRun = z.infer<typeof InterestLastRunSchema>
export type InterestAccrualPositionFund = z.infer<typeof InterestAccrualPositionFundSchema>
export type InterestJournalPreviewLine = z.infer<typeof InterestJournalPreviewLineSchema>
export type ReceivableScheduleRow = z.infer<typeof ReceivableScheduleRowSchema>
export type PayableScheduleRow = z.infer<typeof PayableScheduleRowSchema>
export type InterestAccrualReceivableRow = z.infer<typeof InterestAccrualReceivableRowSchema>
export type InterestAccrualPayableRow = z.infer<typeof InterestAccrualPayableRowSchema>
export type ReceivablePosition = z.infer<typeof ReceivablePositionSchema>
export type PayablePosition = z.infer<typeof PayablePositionSchema>

/** The position tagged with the side it was read for. */
export type InterestAccrualPosition =
  | { side: 'receivable'; data: ReceivablePosition }
  | { side: 'payable'; data: PayablePosition }

export const validateInterestAccrualPosition = (side: InterestSide, data: unknown): InterestAccrualPosition =>
  side === 'receivable'
    ? { side, data: validateWithZodSchema(ReceivablePositionSchema, data, 'interest accrual position (receivable)') }
    : { side, data: validateWithZodSchema(PayablePositionSchema, data, 'interest accrual position (payable)') }

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

export const InterestRunSchema = z
  .object({
    asset_interest_runs__id: z.string(),
    fund_id: z.string().nullish(),
    as_of_date: z.string().nullish(),
    side: InterestSideSchema,
    status_label: z.string().nullish(),
    line_count: z.number().nullish(),
    total_amount_functional: z.number().nullish(),
    notes: z.string().nullish(),
    // payable runs: the Loan Interest (Accrual) fund transaction
    fund_transaction_id: z.string().nullish(),
    date_created: z.string().nullish()
  })
  .passthrough()

// a receivable run's line: one Interest Accrued asset transaction
export const InterestRunReceivableLineSchema = z
  .object({
    asset_transactions__id: z.string(),
    asset_transactions__name: z.string().nullish(),
    tranches__id: z.string().nullish(),
    tranches__name: z.string().nullish(),
    accounts__name: z.string().nullish(),
    currencies__code: z.string().nullish(),
    amount: z.number().nullish(),
    amount_functional: z.number().nullish(),
    status_label: z.string().nullish()
  })
  .passthrough()

// a payable run's line: one loan line of its fund transaction
// (loan__transaction_detail(...).loan_lines)
export const InterestRunPayableLineSchema = z
  .object({
    id: z.string(),
    fund_transaction_id: z.string().nullish(),
    fund_loan_id: z.string().nullish(),
    fund_loan_fee_id: z.string().nullish(),
    component: z.string().nullish(),
    calculated_amount: z.number().nullish(),
    override_amount: z.number().nullish(),
    calculation: z.unknown().nullish(),
    created_by: z.string().nullish(),
    modified_by: z.string().nullish(),
    date_created: z.string().nullish(),
    date_modified: z.string().nullish(),
    trashed: z.boolean().nullish(),
    fund_loans__reference: z.string().nullish(),
    fund_loan_fees__description: z.string().nullish(),
    prog__effective_amount: z.number().nullish()
  })
  .passthrough()

export const InterestRunReceivableDetailSchema = z
  .object({ run: InterestRunSchema, lines: z.array(InterestRunReceivableLineSchema) })
  .passthrough()

export const InterestRunPayableDetailSchema = z
  .object({ run: InterestRunSchema, lines: z.array(InterestRunPayableLineSchema) })
  .passthrough()

const InterestRunEnvelopeSchema = z.object({ run: InterestRunSchema }).passthrough()

export const InterestRunActionResultSchema = z
  .object({
    status: z.string(),
    dry_run: z.boolean().nullish(),
    transactions_affected: z.number().nullish()
  })
  .passthrough()

export type InterestRun = z.infer<typeof InterestRunSchema>
export type InterestRunReceivableLine = z.infer<typeof InterestRunReceivableLineSchema>
export type InterestRunPayableLine = z.infer<typeof InterestRunPayableLineSchema>
export type InterestRunActionResult = z.infer<typeof InterestRunActionResultSchema>

/** A run with its lines, tagged with the run's side. */
export type InterestRunDetail =
  | { side: 'receivable'; run: InterestRun; lines: InterestRunReceivableLine[] }
  | { side: 'payable'; run: InterestRun; lines: InterestRunPayableLine[] }

export const validateInterestRuns = (data: unknown): InterestRun[] =>
  validateWithZodSchema(InterestRunSchema, data, 'interest runs', { isArray: true })

export const validateInterestRunDetail = (data: unknown): InterestRunDetail => {
  // the run's side picks the line shape
  const { run } = validateWithZodSchema(InterestRunEnvelopeSchema, data, 'interest run')
  if (run.side === 'receivable') {
    const detail = validateWithZodSchema(InterestRunReceivableDetailSchema, data, 'interest run detail (receivable)')
    return { side: 'receivable', run: detail.run, lines: detail.lines }
  }
  const detail = validateWithZodSchema(InterestRunPayableDetailSchema, data, 'interest run detail (payable)')
  return { side: 'payable', run: detail.run, lines: detail.lines }
}

export const validateInterestRunActionResult = (data: unknown): InterestRunActionResult =>
  validateWithZodSchema(InterestRunActionResultSchema, data, 'interest run action result')
